import { useState } from "react";
import SelectSearch, { SelectOption } from "../common/forms/SelectSearch";
import QueryView from "../common/lib/QueryView";
import SlideOutButton from "../common/SlideOutButton";
import useReceivingTicketCreated from "../requests/Receiving/useReceivingTicketCreated";
import useArrivedAtBay from "../requests/useArrivedAtBay";
import useArrivedAtLoadout from "../requests/useArrivedAtLoadout";
import useCodeScanned from "../requests/useCodeScanned";
import useDispensingCompleted from "../requests/useDispensingCompleted";
import useTicketCreated from "../requests/useTicketCreated";
import { DevSiteSettingsDto, useGetDevSiteSettings } from "./useGetDevSiteSettings";

type Locations = 
  'CheckIn' |
  'ArrivedAtBay' |
  'ArrivedAtLoadout' |
  'AtOutbound' |
  'DispensingComplete' |
  'TicketCreated' |
  'ReceivingCheckIn' |
  'AtReceivingOutbound' |
  'ReceivingTicketCreated';

interface ScanButtonProps {
  stagedOrderId?: string;
  stagedPurchaseOrderId?: string;
  siteId?: string;
  location: Locations;
}

const emptyId = '00000000-0000-0000-0000-000000000000';

const ScanButton = ({ stagedOrderId, siteId, location, stagedPurchaseOrderId }: ScanButtonProps) => {
  const [scaleId, setScaleId] = useState<undefined | string>(undefined);
  const [bayId, setBayId] = useState<string>(emptyId);

  stagedOrderId = stagedOrderId ?? emptyId;

  const codeScannedDispensing = useCodeScanned(stagedOrderId);
  const dispensingCompleted = useDispensingCompleted(stagedOrderId);
  const ticketCreated = useTicketCreated(stagedOrderId);
  const arrivedAtBay = useArrivedAtBay(stagedOrderId, bayId);
  const arrivedAtLoadout = useArrivedAtLoadout(stagedOrderId, bayId);

  stagedPurchaseOrderId = stagedPurchaseOrderId ?? emptyId;

  const codeScannedReceiving = useCodeScanned(stagedPurchaseOrderId);
  const receivingTicketCreated = useReceivingTicketCreated(stagedPurchaseOrderId);

  const getSiteSettings = useGetDevSiteSettings(siteId ?? '');

  const noScaleOption: SelectOption = { value: emptyId, name: 'No scale' };

  const scaleOptions = (siteSettings: DevSiteSettingsDto) =>
    [noScaleOption, ...siteSettings.scales.map<SelectOption>(s => ({ value: s.id, name: s.name }))];

  const bayOptions = (siteSettings: DevSiteSettingsDto) =>
    siteSettings.bays.map<SelectOption>(s => ({ value: s.id, name: s.name }));

  const request = () => {
    switch (location) {
      case 'CheckIn':
      case 'AtOutbound':
        codeScannedDispensing.request({ scaleId: scaleId === emptyId ? undefined : scaleId });
        break;
      case 'ArrivedAtBay':
        arrivedAtBay.request();
        break;
      case 'ArrivedAtLoadout':
        arrivedAtLoadout.request();
        break;
      case 'TicketCreated':
        ticketCreated.request();
        break;
      case 'DispensingComplete':
        dispensingCompleted.request();
        break;
      case 'ReceivingTicketCreated':
        receivingTicketCreated.request();
        break;
      default:
        codeScannedReceiving.request({ scaleId: scaleId === emptyId ? undefined : scaleId });
    }
  }

  return <>
    <SlideOutButton
      text={
        location === 'DispensingComplete'
          ? "Done Dispensing"
          : location === 'TicketCreated' || location === 'ReceivingTicketCreated'
            ? "Ticket Created"
            : location === 'ArrivedAtBay'
              ? "Arrive at Bay"
              : location === 'ArrivedAtLoadout'
                ? "Arrive at TSA"
                : "Scan"
      }
      onClick={request} />
    {(location === 'CheckIn' || location === 'AtOutbound' || location === 'ReceivingCheckIn' || location === 'AtReceivingOutbound')
      && <QueryView query={getSiteSettings} renderData={siteSettings =>
      <SelectSearch
        id='scale'
        className='dropdown-item pt-0 mb-2'
        placeholder='Select a scale...'
        options={scaleOptions(siteSettings) ?? []}
        onChange={id => setScaleId(id)}
      />}
    />}
    {(location === 'ArrivedAtBay' || location === 'ArrivedAtLoadout') && <QueryView query={getSiteSettings} renderData={siteSettings =>
      <SelectSearch
        id='bay'
        className='dropdown-item pt-0'
        placeholder='Select a bay...'
        options={bayOptions(siteSettings) ?? []}
        onChange={id => setBayId(id)}
      />}
    />}
  </>
}

export default ScanButton;
